import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Calendar as CalendarIcon, Plus, RefreshCw, Search, Trash2, Edit2, X, Star, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HolidayCalendar({ user, onLogout }) {
  const [holidays, setHolidays] = useState({ mandatory: [], restricted: [] });
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'mandatory',
    applicable_to: 'all'
  });

  useEffect(() => {
    fetchHolidays();
  }, [year]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/holidays/${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays({
        mandatory: response.data.mandatory || [],
        restricted: response.data.restricted || []
      });
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  // Combine all holidays for display
  const allHolidays = [
    ...holidays.mandatory.map(h => ({ ...h, type: 'mandatory' })),
    ...holidays.restricted.map(h => ({ ...h, type: 'restricted' }))
  ].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Filter holidays
  const filteredHolidays = allHolidays.filter(holiday => {
    const matchesSearch = !searchTerm || 
      holiday.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || holiday.type === filterType;
    return matchesSearch && matchesType;
  });

  // Statistics
  const stats = {
    total: allHolidays.length,
    mandatory: holidays.mandatory.length,
    restricted: holidays.restricted.length,
    upcoming: allHolidays.filter(h => new Date(h.date) > new Date()).length
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const dayName = new Date(formData.date).toLocaleDateString('en-US', { weekday: 'long' });
      
      const payload = {
        name: formData.name,
        date: formData.date,
        day: dayName,
        type: formData.type,
        applicable_to: formData.type === 'restricted' ? formData.applicable_to : 'all'
      };

      if (selectedHoliday) {
        await axios.put(`${API}/hr-config/holidays/${selectedHoliday.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Holiday updated successfully!');
      } else {
        await axios.post(`${API}/hr-config/holidays`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Holiday added successfully!');
      }
      
      setShowForm(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      name: holiday.name || '',
      date: holiday.date || '',
      type: holiday.type || 'mandatory',
      applicable_to: holiday.applicable_to || 'all'
    });
    setShowForm(true);
  };

  const handleDelete = async (holiday) => {
    if (!window.confirm(`Delete holiday "${holiday.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hr-config/holidays/${holiday.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Holiday deleted successfully!');
      fetchHolidays();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date: '',
      type: 'mandatory',
      applicable_to: 'all'
    });
    setSelectedHoliday(null);
  };

  const getTypeColor = (type) => {
    return type === 'mandatory' 
      ? 'bg-green-100 text-green-700 border-l-green-500'
      : 'bg-blue-100 text-blue-700 border-l-blue-500';
  };

  const getTypeBadgeColor = (type) => {
    return type === 'mandatory' 
      ? 'bg-green-500 text-white'
      : 'bg-blue-500 text-white';
  };

  const canEdit = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'hr manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>📅 Holiday Calendar {year}</h1>
            <p className="text-gray-600 mt-2">Zobox Protech Limited - Official Holidays</p>
          </div>
          <div className="flex gap-3">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {[2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={fetchHolidays}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canEdit && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: '#2b3380'}}
              >
                <Plus className="w-5 h-5" />
                Add Holiday
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <CalendarIcon className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Holidays</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <Star className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.mandatory}</p>
              <p className="text-sm text-gray-500">Mandatory</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.restricted}</p>
              <p className="text-sm text-gray-500">Restricted</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <CalendarIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{stats.upcoming}</p>
              <p className="text-sm text-gray-500">Upcoming</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search holidays..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="mandatory">Mandatory Only</option>
              <option value="restricted">Restricted Only</option>
            </select>
          </div>
        </div>

        {/* Holidays List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading holidays...</p>
          </div>
        ) : filteredHolidays.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <CalendarIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No holidays found</p>
            <p className="text-gray-400">
              {searchTerm || filterType !== 'all' 
                ? 'Try different search terms or filters' 
                : canEdit ? 'Click "Add Holiday" to create one' : 'No holidays configured for this year'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredHolidays.map((holiday, index) => {
              const holidayDate = new Date(holiday.date);
              const isPast = holidayDate < new Date();
              return (
                <div 
                  key={holiday.id || index} 
                  className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getTypeColor(holiday.type)} ${isPast ? 'opacity-60' : ''}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>{holiday.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeBadgeColor(holiday.type)}`}>
                          {holiday.type === 'mandatory' ? 'MANDATORY' : 'RESTRICTED'}
                        </span>
                        {isPast && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            PAST
                          </span>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(holiday)}
                          className="p-2 hover:bg-blue-50 rounded text-blue-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(holiday)}
                          className="p-2 hover:bg-red-50 rounded text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {holidayDate.toLocaleDateString('en-IN', { 
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                      {holiday.type === 'restricted' && holiday.applicable_to && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                          Applicable to: {holiday.applicable_to}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* HR Policy Notes */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold mb-3" style={{color: '#2b3380'}}>📌 HR Policy Notes</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Islamic holidays are tentative and subject to moon sighting</li>
            <li>• Holidays falling on Sunday are not shifted</li>
            <li>• Restricted holidays are not carry-forward, not encashable</li>
            <li>• Employees may avail restricted holidays as per religion</li>
            <li>• Final applicability subject to state notifications & company policy</li>
          </ul>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedHoliday ? '✏️ Edit Holiday' : '📅 Add New Holiday'}
                  </h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Holiday Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Republic Day"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Date *</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Holiday Type *</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="mandatory">Mandatory (Company-wide)</option>
                        <option value="restricted">Restricted (Religion-specific)</option>
                      </select>
                    </div>

                    {formData.type === 'restricted' && (
                      <div>
                        <label className="block text-sm font-medium mb-2">Applicable To</label>
                        <select
                          value={formData.applicable_to}
                          onChange={(e) => setFormData({...formData, applicable_to: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="all">All Employees</option>
                          <option value="hindu">Hindu</option>
                          <option value="muslim">Muslim</option>
                          <option value="christian">Christian</option>
                          <option value="sikh">Sikh</option>
                          <option value="jain">Jain</option>
                          <option value="buddhist">Buddhist</option>
                        </select>
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Mandatory holidays apply to all employees. Restricted holidays are optional and specific to certain groups.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <CalendarIcon className="w-5 h-5" />
                        {submitting ? 'Saving...' : (selectedHoliday ? 'Update Holiday' : 'Add Holiday')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HolidayCalendar;
